const FALLBACK_MONOSPACE_FONT = 'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace'
const FALLBACK_FONT_SIZE = '12px'
const FALLBACK_FONT_STRING = `normal normal ${FALLBACK_FONT_SIZE} ${FALLBACK_MONOSPACE_FONT}`

// Shared canvas context for measuring monospace text width without triggering layout reflow.
let measureContext: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null
let cachedFontString: string | null = null
let cachedLetterSpacing: number = 0
let usingFallbackFont = false
let canvasSupportsLetterSpacing: boolean | null = null
let themeObserver: MutationObserver | null = null

function getContext(): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null {
  if (measureContext) {
    // If we previously fell back to defaults because no code element existed yet,
    // retry now that one may be in the DOM.
    if (usingFallbackFont) {
      resolveFontProperties()
      if (!usingFallbackFont) {
        applyFontToContext()
      }
    }
    return measureContext
  }
  if (typeof OffscreenCanvas !== 'undefined') {
    measureContext = new OffscreenCanvas(0, 0).getContext('2d')
  } else if (typeof document !== 'undefined') {
    measureContext = document.createElement('canvas').getContext('2d')
  }
  if (measureContext) {
    // Cache letter-spacing feature detection once
    canvasSupportsLetterSpacing = 'letterSpacing' in measureContext

    resolveFontProperties()
    applyFontToContext()

    // Watch for theme changes to auto-invalidate cached font properties
    observeThemeChanges()
  }
  return measureContext
}

/**
 * Apply the cached font string and letter-spacing to the canvas context.
 */
function applyFontToContext(): void {
  if (!measureContext || !cachedFontString) return
  measureContext.font = cachedFontString

  // Set letter-spacing if supported (Chrome 94+, Firefox 125+)
  if (cachedLetterSpacing !== 0 && canvasSupportsLetterSpacing) {
    ;(measureContext as CanvasRenderingContext2D).letterSpacing = `${cachedLetterSpacing}px`
  }
}

/**
 * Watch for GitHub theme attribute changes on <html> and invalidate the
 * cached font properties so the next measurement picks up the new styles.
 */
function observeThemeChanges(): void {
  if (themeObserver || typeof document === 'undefined') return

  themeObserver = new MutationObserver(() => {
    // Theme attributes changed — invalidate font cache and re-resolve
    cachedFontString = null
    cachedLetterSpacing = 0
    usingFallbackFont = false
    if (measureContext) {
      resolveFontProperties()
      applyFontToContext()
    }
  })
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-color-mode', 'data-dark-theme', 'data-light-theme'],
  })
}

/**
 * Resolve font properties by reading computed style from an actual code text element.
 * Looks for both the blob renderer's `.react-code-text` and the code-nav's
 * `.react-code-lines code` / `.react-blob-print-hide code` selectors.
 */
function resolveFontProperties(): void {
  if (cachedFontString && !usingFallbackFont) return

  const style = getCodeElementStyle()
  if (style) {
    const fontStyle = style.fontStyle || 'normal'
    const fontWeight = style.fontWeight || 'normal'
    const fontSize = style.fontSize || FALLBACK_FONT_SIZE
    const fontFamily = style.fontFamily || FALLBACK_MONOSPACE_FONT
    const letterSpacingStr = style.letterSpacing

    // Parse letter-spacing (e.g., "0px", "1px", "normal")
    cachedLetterSpacing = 0
    if (letterSpacingStr && letterSpacingStr !== 'normal') {
      cachedLetterSpacing = parseFloat(letterSpacingStr) || 0
    }

    // Canvas font format: [font-style] [font-weight] font-size font-family
    cachedFontString = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`
    usingFallbackFont = false
  } else {
    cachedFontString = FALLBACK_FONT_STRING
    usingFallbackFont = true
  }
}

function getCodeElementStyle(): CSSStyleDeclaration | null {
  if (typeof document !== 'undefined') {
    const codeElement = document.querySelector('.react-code-text, .react-code-lines code, .react-blob-print-hide code')
    if (codeElement) {
      return getComputedStyle(codeElement)
    }
  }
  return null
}

/**
 * Measure the pixel width of a string using the Canvas `measureText` API.
 * Uses the same monospace font as code lines. Returns 0 if no canvas
 * context is available (e.g. in tests or SSR).
 *
 * @param text - The string to measure.
 * @param tabSize - Optional tab stop width in spaces. When provided, tab characters
 *   are expanded to spaces before measuring (Canvas `measureText` does not
 *   handle tab stops natively). Callers dealing with raw source lines should
 *   pass this value.
 *
 * Accounts for letter-spacing: on browsers that support canvas `letterSpacing`,
 * it is set on the context directly; on older browsers, letter-spacing is added
 * manually.
 */
export function measureMonospaceTextWidth(text: string, tabSize?: number): number {
  const ctx = getContext()
  if (!ctx) return 0

  // Expand tabs to spaces if tabSize is specified — Canvas doesn't handle tab stops
  const measuredText = tabSize != null ? text.replace(/\t/g, ' '.repeat(tabSize)) : text

  let width = ctx.measureText(measuredText).width

  // For browsers that don't support letterSpacing on canvas context,
  // manually add letter-spacing contribution.
  // CSS letter-spacing applies between characters: N chars => N-1 gaps.
  if (cachedLetterSpacing !== 0 && !canvasSupportsLetterSpacing && measuredText.length > 1) {
    width += cachedLetterSpacing * (measuredText.length - 1)
  }

  return width
}

/**
 * Reset cached state. Useful for tests and when font/theme changes.
 */
export function resetMeasureContext(): void {
  measureContext = null
  cachedFontString = null
  cachedLetterSpacing = 0
  usingFallbackFont = false
  canvasSupportsLetterSpacing = null
  if (themeObserver) {
    themeObserver.disconnect()
    themeObserver = null
  }
}
